import {
    useCallback,
    useMemo,
} from 'react'
import Box from '@mui/material/Box'
import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
} from '@mui/x-data-grid-pro'

import useLabel from 'app/hooks/useLabel'
import useEnumValues from 'app/hooks/useEnumValues'
import ContainerStatusEnriched from 'app/common/DomainObject/ComponentMapper/ContainerStatusEnriched'
import InputSingleline from 'app/shared-components/InputSingleline'
import Checkbox from 'app/shared-components/Checkbox'
import Select from 'app/shared-components/Select'
import {
    RejectionReason,
} from 'app/enums/label.enums'
import BooleanReader from 'app/shared-components/BooleanReader'

import {
    BookedContainer,
} from '../bookedContainers.types'
import useStyles from './BookedContainersTable.style'

type Props = {
    data: BookedContainer[],
    onChange: (newValue: BookedContainer) => void,
    disabled: boolean,
    isInternalType: boolean,
}

const BookedContainersTable = ({
    data,
    onChange,
    disabled,
    isInternalType,
}: Props) => {
    const {
        classes,
    } = useStyles()
    const l = useLabel()
    const rejectionReasons = useEnumValues()(RejectionReason)

    const handleCellChange = useCallback((
        row: BookedContainer,
        field: string,
        newFieldValue: unknown,
    ) => {
        const updatedRow = {
            ...row,
            [field]: newFieldValue,
        }

        onChange(updatedRow)
    }, [onChange])

    const columns: GridColDef<BookedContainer>[] = useMemo(() => {
        const baseColumns: GridColDef<BookedContainer>[] = [
            {
                field: 'selected',
                headerName: '',
                width: 60,
                sortable: false,
                filterable: false,
                resizable: false,
                renderCell: (params: GridRenderCellParams<BookedContainer>) => {
                    const {
                        row,
                    } = params

                    return (
                        <Checkbox
                            name="selected"
                            value={row.selected}
                            onChange={(newFieldValue) => {
                                handleCellChange(row, 'selected', newFieldValue)
                            }}
                            className={classes.checkbox}
                            disabled={disabled}
                        />
                    )
                },
            },
            {
                field: 'serialNumber',
                headerName: l('SERIAL_NUMBER'),
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<BookedContainer>) => {
                    const {
                        row,
                    } = params

                    return (
                        <InputSingleline
                            name="serialNumber"
                            value={row.serialNumber}
                            classNames={{
                                label: classes.inputLabel,
                            }}
                            disabled
                        />
                    )
                },
            },
            {
                field: 'jypId',
                headerName: l('JYP_ID'),
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<BookedContainer>) => {
                    const {
                        row,
                    } = params

                    return (
                        <InputSingleline
                            name="jypId"
                            value={row.jypId}
                            classNames={{
                                label: classes.inputLabel,
                            }}
                            disabled
                        />
                    )
                },
            },
            {
                field: 'containerStatusEnriched',
                headerName: l(isInternalType ? 'CURRENT_STATUS' : 'STATUS'),
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<BookedContainer>) => {
                    const {
                        row,
                    } = params

                    return (
                        <ContainerStatusEnriched
                            value={row.containerStatusEnriched}
                            rowProps={{
                                containerStatus: row.containerStatusEnriched,
                            }}
                        />
                    )
                },
            },
        ]

        if (isInternalType) {
            baseColumns.push({
                field: 'statusAtBooking',
                headerName: l('STATUS_AT_BOOKING'),
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<BookedContainer>) => {
                    const {
                        row,
                    } = params

                    return (
                        <ContainerStatusEnriched
                            value={row.statusAtBooking}
                            rowProps={{
                                containerStatus: row.statusAtBooking,
                            }}
                        />
                    )
                },
            })
        }

        baseColumns.push(
            {
                field: 'tempRange',
                headerName: l('RANGE'),
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<BookedContainer>) => {
                    const {
                        row,
                    } = params

                    return (
                        <InputSingleline
                            name="tempRange"
                            value={row.tempRange}
                            classNames={{
                                label: classes.inputLabel,
                            }}
                            disabled
                        />
                    )
                },
            },
            {
                field: 'containerTypeContainerModel',
                headerName: l('CONTAINER_MODEL'),
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<BookedContainer>) => {
                    const {
                        row,
                    } = params

                    return (
                        <InputSingleline
                            name="containerTypeContainerModel"
                            value={row.containerTypeContainerModel}
                            classNames={{
                                label: classes.inputLabel,
                            }}
                            disabled
                        />
                    )
                },
            },
            {
                field: 'isRejected',
                headerName: l('IS_REJECTED'),
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<BookedContainer>) => {
                    const {
                        row,
                    } = params

                    return (
                        <BooleanReader
                            name="isRejected"
                            value={row.isRejected}
                            classNames={{
                                label: classes.inputLabel,
                            }}
                        />
                    )
                },
            },
            {
                field: 'rejectionReason',
                headerName: l('REJECTION_REASON'),
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<BookedContainer>) => {
                    const {
                        row,
                    } = params

                    return (
                        <Select
                            name="rejectionReason"
                            value={row.rejectionReason}
                            onChange={(newFieldValue) => {
                                handleCellChange(row, 'rejectionReason', newFieldValue)
                            }}
                            allowedValues={rejectionReasons}
                            disabled={disabled}
                            required
                        />
                    )
                },
            },
            {
                field: 'rejectionComment',
                headerName: l('REJECTION_COMMENT'),
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<BookedContainer>) => {
                    const {
                        row,
                    } = params

                    return (
                        <InputSingleline
                            name="rejectionComment"
                            value={row.rejectionComment}
                            onChange={(newFieldValue) => {
                                handleCellChange(row, 'rejectionComment', newFieldValue)
                            }}
                            classNames={{
                                label: classes.inputLabel,
                            }}
                            className={classes.input}
                            disabled={disabled}
                            required={row.rejectionReason === RejectionReason.REJECTION_OTHER}
                        />
                    )
                },
            },
        )

        return baseColumns
    }, [
        l,
        isInternalType,
        classes.checkbox,
        classes.inputLabel,
        classes.input,
        disabled,
        rejectionReasons,
        handleCellChange,
    ])

    return (
        <Box className={classes.container}>
            <DataGridPro
                rows={data}
                columns={columns}
                disableRowSelectionOnClick
                hideFooter
                autoHeight
                density="compact"
                disableColumnMenu
                getRowHeight={() => { return 'auto' }}
                getRowId={(row) => {
                    return row.id
                }}
                localeText={{
                    noRowsLabel: l('THERE_IS_NO_DATA_IN_THIS_TABLE_YET'),
                }}
            />
        </Box>
    )
}

export default BookedContainersTable
